import { z } from 'zod'
import { moneySchema, optionalText, pageQuerySchema, versionSchema } from './common.js'
import type { ProjectUserRef } from './projects.js'
import type { CostCategoryRef, ProjectRefLite, WbsNodeRef } from './projects-costs.js'

/**
 * Projects release P6: time and T&M (docs/refactor/projects/03-billing-and-time.md §10, P6_BREAKDOWN.md).
 * People log minutes against a project (optionally a plan node and a task); approvers decide them in
 * batches; approval captures the cost rate (the person's, `projects.rates` only) and the bill rate (the
 * member's or their rate role's, a price shown with `projects.financials`). Called "Time", never
 * "Timesheet" (HRM attendance uses that word).
 */

const requiredText = (max: number, message = 'Required') => z.string().trim().min(1, message).max(max)
const nullableUuid = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)
const nonNegativeMoney = moneySchema.refine((v) => Number(v) >= 0, 'Cannot be negative')

// ── settings (group `projects`, key `time`) ─────────────────────────────────

export const TIME_WEEK_STARTS = ['SATURDAY', 'SUNDAY', 'MONDAY'] as const
export type TimeWeekStart = (typeof TIME_WEEK_STARTS)[number]

export const projectTimeSettingsSchema = z.object({
  /** Entries may be dated up to this many days ahead (planned leave, travel). */
  futureDays: z.number().int().min(0).max(31).default(7),
  /** Entries dated before today − this many days can't be created, edited or submitted (approval still works). */
  lockAfterDays: z.number().int().min(1).max(366).default(30),
  /** Every entry names a task of the project. */
  requireTask: z.boolean().default(false),
  /** A working day's hours, for the expected hours of allocated members (TIME_NOT_SUBMITTED, utilisation). */
  hoursPerDay: z.number().min(1).max(24).multipleOf(0.25).default(8),
  /** The first day of the week grid and of the approval groups (06 §5.9). */
  weekStart: z.enum(TIME_WEEK_STARTS).default('SATURDAY'),
})
export type ProjectTimeSettings = z.output<typeof projectTimeSettingsSchema>
export const DEFAULT_PROJECT_TIME_SETTINGS: ProjectTimeSettings = projectTimeSettingsSchema.parse({})

/** The start of the week `date` falls in (YYYY-MM-DD in, out). */
export function timeWeekStartOf(date: string, weekStart: TimeWeekStart): string {
  const d = new Date(`${date}T00:00:00Z`)
  const first = { SUNDAY: 0, MONDAY: 1, SATURDAY: 6 }[weekStart]
  const back = (d.getUTCDay() - first + 7) % 7
  d.setUTCDate(d.getUTCDate() - back)
  return d.toISOString().slice(0, 10)
}

/** Minutes as hours with up to two decimals ("7.5"), for display and CSV. */
export function minutesToHours(minutes: number): string {
  return (Math.round((minutes / 60) * 100) / 100).toString()
}

// ── rate roles (`/project-roles`) ────────────────────────────────────────────

export interface RateRoleRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}

/** `POST /project-roles`, `PATCH …/:id` (projects.configure). */
export const rateRoleInputSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9_.-]+$/, 'Letters, digits, dot, dash and underscore'),
  name: requiredText(120),
  nameAr: optionalText(120),
  /**
   * Per hour, ex-VAT; null: no bill rate (members need their own). Omitted on create: none; omitted on update:
   * kept (a reader without projects.financials can't see it, so never sends it).
   */
  billRate: nonNegativeMoney.nullable().optional(),
})
export type RateRoleInput = z.input<typeof rateRoleInputSchema>
export const rateRoleUpdateSchema = rateRoleInputSchema.extend({ version: versionSchema })

export interface ProjectRateRole extends RateRoleRef {
  /** null when the role has none, and always without projects.financials. */
  billRate: string | null
  archived: boolean
  /** Active members on this role. */
  memberCount: number
  version: number
}

// ── person cost rates (`/project-rates/people`, projects.rates only) ───────────

/** `PUT /project-rates/people/:userId`: adds the rate from a date, or replaces the one of that same date. */
export const personRateInputSchema = z.object({
  /** Per hour; what the company's hour of this person costs. */
  costRate: nonNegativeMoney,
  effectiveFrom: z.iso.date(),
  note: optionalText(500),
})
export type PersonRateInput = z.input<typeof personRateInputSchema>

export interface PersonRate {
  costRate: string
  effectiveFrom: string
  note: string | null
  by: ProjectUserRef | null
  at: string
}

/** `GET /project-rates/people?q&page&pageSize&missing`: people who are members of an open project or have a rate. */
export const personRatesQuerySchema = pageQuerySchema.extend({
  /** Only people with no rate today. */
  missing: z.stringbool().optional(),
})
export type PersonRatesQuery = z.input<typeof personRatesQuerySchema>
export interface PersonRateRow {
  user: ProjectUserRef
  /** The rate in force today; null: none (their approved time costs 0, `costRateMissing`). */
  current: { costRate: string; effectiveFrom: string } | null
  /** A later rate already entered. */
  next: { costRate: string; effectiveFrom: string } | null
  /** Open projects the person is an active member of. */
  activeProjects: number
}
/** `GET /project-rates/people/:userId`: the history, newest first. */
export interface PersonRateHistory {
  user: ProjectUserRef
  rates: PersonRate[]
}

// ── time entries (`/project-time/entries`) ───────────────────────────────────

export const TIME_ENTRY_STATUSES = ['DRAFT', 'SUBMITTED', 'APPROVED', 'RETURNED'] as const
export type TimeEntryStatus = (typeof TIME_ENTRY_STATUSES)[number]

const entryFields = z.object({
  projectId: z.uuid(),
  /** A node of the project (optional). */
  wbsNodeId: nullableUuid,
  /** A task linked to the project and visible to the person; required with `time.requireTask`. */
  taskId: nullableUuid,
  workDate: z.iso.date(),
  /** 1 … 1440; the person's day may not exceed 24 hours (422 on `minutes`). */
  minutes: z.number().int().min(1, 'Enter the time').max(1440),
  /** Omitted: true where the project's time-and-materials function is on, else false. Only T&M projects bill. */
  billable: z.boolean().optional(),
  note: optionalText(500),
})
/** `POST /project-time/entries` (projects.time): the person's own entry, DRAFT. */
export const timeEntryInputSchema = entryFields
export type TimeEntryInput = z.input<typeof timeEntryInputSchema>
/** `PATCH /project-time/entries/:id` (DRAFT or RETURNED, own). */
export const timeEntryUpdateSchema = entryFields.extend({ version: versionSchema })
export type TimeEntryUpdate = z.input<typeof timeEntryUpdateSchema>

/**
 * `POST /project-time/entries/batch` (the week grid's save): upserts and deletes of the person's own
 * entries, all or nothing (422 naming the failing items by index: `upserts.3.minutes`).
 */
export const timeEntriesBatchSchema = z
  .object({
    upserts: z
      .array(
        entryFields.extend({
          /** Absent: a new entry. */
          id: z.uuid().optional(),
          version: versionSchema.optional(),
        }),
      )
      .max(200)
      .default([]),
    deletes: z
      .array(z.object({ id: z.uuid(), version: versionSchema }))
      .max(200)
      .default([]),
  })
  .refine((v) => v.upserts.length + v.deletes.length > 0, {
    path: ['upserts'],
    message: 'Nothing to save',
  })
  .refine((v) => v.upserts.every((u) => (u.id === undefined) === (u.version === undefined)), {
    path: ['upserts'],
    message: 'An existing entry needs its version',
  })
export type TimeEntriesBatch = z.input<typeof timeEntriesBatchSchema>
export interface TimeEntriesBatchResult {
  entries: TimeEntry[]
  deleted: number
}

/** `POST /project-time/entries/submit`: these own entries, or every DRAFT/RETURNED one of the week. */
export const timeSubmitSchema = z
  .object({
    ids: z.array(z.uuid()).max(500).optional(),
    weekStart: z.iso.date().optional(),
  })
  .refine((v) => (v.ids !== undefined) !== (v.weekStart !== undefined), {
    path: ['ids'],
    message: 'Submit either chosen entries or a week',
  })
export type TimeSubmitInput = z.input<typeof timeSubmitSchema>
export interface TimeSubmitResult {
  submitted: number
  minutes: number
}

export const TIME_ENTRY_SORT_FIELDS = ['workDate', 'minutes', 'status'] as const
/**
 * `GET /project-time/entries`: the viewer's own entries by default. With `projectId` (or `userId` of
 * someone else) it lists what the viewer may approve: projects.time_approve in scope, or the project's
 * MANAGER members.
 */
export const timeEntriesQuerySchema = pageQuerySchema.extend({
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  projectId: z.uuid().optional(),
  userId: z.uuid().optional(),
  status: z.enum(TIME_ENTRY_STATUSES).optional(),
  taskId: z.uuid().optional(),
})
export type TimeEntriesQuery = z.input<typeof timeEntriesQuerySchema>

export interface TimeTaskRef {
  id: string
  number: string
  /** null when the viewer can't see the task (it shows as restricted). */
  title: string | null
}

export interface TimeEntry {
  id: string
  user: ProjectUserRef
  project: ProjectRefLite
  wbsNode: WbsNodeRef | null
  task: TimeTaskRef | null
  workDate: string
  minutes: number
  billable: boolean
  note: string | null
  status: TimeEntryStatus
  submittedAt: string | null
  decidedAt: string | null
  decidedBy: ProjectUserRef | null
  /** The latest return's reason while RETURNED. */
  returnReason: string | null
  /** Captured at approval (null before). Cost: only with projects.rates. */
  costRate: string | null
  costAmount: string | null
  /** The person had no cost rate on the work date: the entry costs 0 (ASM-198). Only with projects.rates. */
  costRateMissing: boolean | null
  /** Captured at approval when billable. Only with projects.financials. */
  billRate: string | null
  billAmount: string | null
  /** In an active T&M invoice line. */
  billed: boolean
  /** Moved to the books by a LABOUR_POSTING cost entry. */
  labourPosted: boolean
  can: { edit: boolean; delete: boolean; submit: boolean; approve: boolean; return: boolean }
  version: number
}

/**
 * The projects the viewer may log time on now, in `GET /project-time/context` (active member, projects.time,
 * open, Time on), with the defaults an entry takes there.
 */
export interface TimeProjectOption {
  project: ProjectRefLite
  /** The `billable` default and whether the person may change it. */
  billableDefault: boolean
  billableEditable: boolean
  requireTask: boolean
}
export interface TimeContext {
  settings: ProjectTimeSettings
  /** Today (Muscat), the earliest date that can still be edited, and the latest that can be logged. */
  today: string
  editableFrom: string
  latestDate: string
  projects: TimeProjectOption[]
}

// ── approvals (`/project-time/approvals`) ────────────────────────────────────

/** `GET /project-time/approvals?projectId&userId&from&to`: SUBMITTED entries the viewer may decide, by person and week. */
export const timeApprovalsQuerySchema = z.object({
  projectId: z.uuid().optional(),
  userId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type TimeApprovalsQuery = z.input<typeof timeApprovalsQuerySchema>
export interface TimeApprovalGroup {
  user: ProjectUserRef
  weekStart: string
  minutes: number
  billableMinutes: number
  entries: TimeEntry[]
  /** SELF_APPROVAL: the viewer's own entries (listed for a manager who also logs, never decidable). */
  blocked: 'SELF_APPROVAL' | null
}
export interface TimeApprovals {
  groups: TimeApprovalGroup[]
  /** Entries waiting in total (also outside the filter), for the nav badge. */
  waiting: number
  /** More entries match than one answer carries (TIME_APPROVALS_LIMIT): narrow the filter. Optional for fixtures. */
  truncated?: boolean
}
/** At most this many entries in one approvals answer. */
export const TIME_APPROVALS_LIMIT = 2000

/**
 * The versions the approver saw, by entry id (P6 decision 32): an entry changed since (returned and
 * resubmitted with other hours) answers 409 VERSION_CONFLICT instead of being decided unseen. Optional.
 */
const seenVersions = z.record(z.uuid(), versionSchema).optional()

/** `POST /project-time/approvals/approve {ids}`: all or nothing. */
export const timeApproveSchema = z.object({ ids: z.array(z.uuid()).min(1).max(500), versions: seenVersions })
/**
 * `POST /project-time/approvals/return {ids, reason}`: SUBMITTED entries, or APPROVED ones not yet billed
 * nor labour-posted (their captured rates are cleared). All or nothing.
 */
export const timeReturnSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(500),
  reason: requiredText(500, 'Say what to change'),
  versions: seenVersions,
})
export type TimeReturnInput = z.input<typeof timeReturnSchema>
export interface TimeDecisionResult {
  decided: number
  minutes: number
}

// ── the project's Time tab and the task summary ──────────────────────────────

/** `GET /projects/:id/time?from&to` (projects.read in scope): hours by person and week. */
export const projectTimeQuerySchema = z.object({
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
})
export type ProjectTimeQuery = z.input<typeof projectTimeQuerySchema>
export interface ProjectTimeRow {
  user: ProjectUserRef
  weekStart: string
  draftMinutes: number
  submittedMinutes: number
  approvedMinutes: number
  billableMinutes: number
  /** Approved cost: only with projects.rates. */
  costAmount: string | null
  /** Approved billable value: only with projects.financials. */
  billAmount: string | null
}
export interface ProjectTime {
  from: string
  to: string
  rows: ProjectTimeRow[]
  totals: Omit<ProjectTimeRow, 'user' | 'weekStart'>
  /** Approved time cost of the project to date (all people): with projects.financials. */
  labourCostToDate: string | null
  can: { approve: boolean }
}

/** `GET /project-time/tasks/:taskId/summary`: for viewers who see the task's project; 404 otherwise. */
export interface TaskTimeSummary {
  taskId: string
  projectId: string
  approvedMinutes: number
  submittedMinutes: number
  people: number
}

// ── labour posting (03 §10.7) ────────────────────────────────────────────────

const labourFields = z.object({
  /** APPROVED entries with no labour posting, worked on or before this date. */
  through: z.iso.date(),
  /** Only this project's entries. */
  projectId: nullableUuid,
  /** The cost entry's date (the journal date). */
  entryDate: z.iso.date(),
  /**
   * Credited (P6 decision 25): an active, postable LIABILITY (salaries payable, accrued wages, a labour
   * clearing account) or EXPENSE account (a reclass from salaries expense, a "labour absorbed" contra);
   * never revenue, an asset or equity, cash or bank, an account documents keep or a cost category's own
   * (`GET /accounts/options?purpose=labour_credit` lists the same). 422 on `creditAccountId` otherwise.
   */
  creditAccountId: z.uuid(),
  description: requiredText(500, 'Describe the posting'),
})
/** `POST /project-cost-entries/labour/preview` (projects.cost_entries + projects.rates): writes nothing. */
export const labourPostingPreviewSchema = labourFields
/**
 * `POST /project-cost-entries/labour`: a LABOUR_POSTING cost entry (`CostEntry`), idempotent per requestId:
 * a retry with the same fields returns it; the id reused with another `through`, `projectId`, date,
 * account or description is 422 on `requestId`.
 */
export const labourPostingSchema = labourFields.extend({ requestId: z.uuid() })
export type LabourPostingInput = z.input<typeof labourPostingSchema>
export interface LabourPostingPreview {
  lines: {
    project: ProjectRefLite
    wbsNode: WbsNodeRef | null
    costCategory: CostCategoryRef
    minutes: number
    amount: string
    entryCount: number
  }[]
  total: string
  entryCount: number
  /** Entries costing 0 because the person had no rate (they are posted at 0 and marked). */
  missingRates: number
}

// ── errors ───────────────────────────────────────────────────────────────────

export const PROJECT_TIME_ERROR_CODES = [
  /** 422: only active members log time on a project. */
  'PROJECT_NOT_MEMBER',
  /** 422: nobody decides their own time. */
  'SELF_APPROVAL',
  /** 422: the entry is in an active T&M invoice line (void the invoice first). */
  'TIME_ENTRY_BILLED',
  /** 422: the entry is in a LABOUR_POSTING cost entry (void it first). */
  'TIME_ENTRY_POSTED',
  /** 422: the entry's status doesn't allow this (edit after submit, approve a draft). */
  'TIME_ENTRY_ILLEGAL_MOVE',
  /** 422: dated before the lock (`time.lockAfterDays`) or after `time.futureDays`. */
  'TIME_DATE_LOCKED',
] as const
export type ProjectTimeErrorCode = (typeof PROJECT_TIME_ERROR_CODES)[number]
